/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "LOG" TAB                              *
 *  File: apps/web/src/pages/bridge/LogTab.js                      *
 *  What it does: every delivery, decision and key change for this *
 *  company, newest first, in plain words. The log is append-only; *
 *  this screen only reads it.                                     *
 *  Reads: GET /log.  Writes: nothing.                             *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { BRIDGE_WORDS, LOG_ACTION_NAMES } from './bridgeWords';
import { LoadState, ToneChip, formatWhen } from './bridgeParts';
import { fetchLog } from './bridgeApi';

// How each result looks (word + icon + colour, never colour alone).
const RESULT_LOOKS = {
  ok: { tone: 'ok', icon: 'check', word: 'Done' },
  rejected: { tone: 'bad', icon: 'cross', word: 'Refused' },
  failed: { tone: 'warn', icon: 'alert', word: 'Needs a person' },
};

/* ---- describeLogRow(logRow) -- one line of plain words ---- */
function describeLogRow(logRow) {
  const counts = logRow.counts;
  const detail = logRow.detail || {};

  if (counts) {
    return counts.received + ' received: ' + counts.new + ' new, '
      + counts.unchanged + ' unchanged, ' + counts.errors + ' with problems.';
  }

  if (detail.why) {
    return '"' + detail.why + '"' + (detail.reason ? ' — ' + detail.reason : '');
  }

  return detail.reason || detail.name || '';
}

export default function LogTab() {
  const [logRows, setLogRows] = useState([]);            // the log lines
  const [loadState, setLoadState] = useState('loading'); // loading / ready / error

  /* ---- loadLog() ---- */
  const loadLog = useCallback(async () => {
    setLoadState('loading');

    const answer = await fetchLog();

    if (!answer.ok) {
      setLoadState('error');
      return;
    }

    setLogRows(answer.data || []);
    setLoadState('ready');
  }, []);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  return (
    <section aria-labelledby="zb-tab-log-title">
      <h2 id="zb-tab-log-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabLog}</h2>

      {loadState !== 'ready' ? <LoadState state={loadState} onRetry={loadLog} /> : null}
      {loadState === 'ready' && logRows.length === 0 ? <LoadState state="empty" emptyWord={BRIDGE_WORDS.logEmpty} /> : null}

      <ul className="zb-list">
        {logRows.map((oneRow) => {
          const look = RESULT_LOOKS[oneRow.result] || RESULT_LOOKS.ok;

          return (
            <li className="zb-list-row zb-list-row-stack" key={oneRow.id}>
              <div className="zb-list-main">
                <strong>{LOG_ACTION_NAMES[oneRow.action] || oneRow.action}</strong>
                <ToneChip tone={look.tone} icon={look.icon} word={look.word} />
              </div>
              <p className="zb-small">{formatWhen(oneRow.happenedAt)} · {describeLogRow(oneRow)}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
